import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useRematchIngredient, useStores } from '../../api/coverage';
import type { StoreRematch } from '../../api/coverage';
import { useMatchQueue } from '../../api/review';
import type { MatchQueueRow } from '../../api/review';
import IngredientEditor from './IngredientEditor';
import ProductFinder from './ProductFinder';
import { MatchRow, Pager } from './shared';

const PAGE_SIZE = 50;

/** Why a shop came back with nothing, in the four ways it can happen.
 *
 * "Nothing matched here yet" covers all four and answers none of them, and
 * they want opposite things done: a shop that was never asked wants asking
 * again, a shop that answered and had its answers turned down wants a product
 * picked by hand, and a shop that genuinely lists nothing wants another name.
 */
function whatHappened(shop: StoreRematch): string {
  const products = (count: number) => `${count} product${count === 1 ? '' : 's'}`;
  if (shop.error) return `${shop.store} could not be asked (${shop.error})`;
  if (shop.matched > 0) return '';
  if (shop.unjudged > 0) return `${shop.store}: ${products(shop.unjudged)} found, none judged`;
  if (shop.rejected > 0)
    return `${shop.store}: ${products(shop.rejected)} found, none of them this`;
  return `${shop.store} found nothing for ${shop.searched.map((term) => `“${term}”`).join(', ')}`;
}

function IngredientBlock({ row, storeId }: { row: MatchQueueRow; storeId: number | null }) {
  /* Saving a name is only half the fix: what makes it worth anything is a
     shop being searched for it, and that ran once a night. So it runs here,
     at every shop, the moment the names are saved -- otherwise a person edits
     an alias, sees the row unchanged, and cannot tell a name that worked from
     one that did not until the next morning. */
  const rematch = useRematchIngredient();
  const empty = (rematch.data?.stores ?? []).map(whatHappened).filter(Boolean);

  return (
    <article className="match-block">
      <header className="match-block__head">
        <h2>{row.name}</h2>
        <span className="muted small">
          #{row.id} · {row.uses.toLocaleString()} recipe{row.uses === 1 ? '' : 's'}
          {row.parent && ` · a kind of ${row.parent}`}
          {row.aliases.length > 0 && ` · also “${row.aliases.join('”, “')}”`}
        </span>
      </header>

      {row.substitution && (
        <p className="match-block__substitution">
          {row.substitution.components.length > 0 && (
            <>Make it from {row.substitution.components.join(', ')}</>
          )}
          {row.substitution.components.length > 0 &&
            row.substitution.substitutes.length > 0 &&
            ' · '}
          {row.substitution.substitutes.length > 0 && (
            <>Or buy {row.substitution.substitutes.join(', ')}</>
          )}
        </p>
      )}

      {rematch.isPending && <p className="muted small">Matching ingredients…</p>}

      {!rematch.isPending && row.matches.length === 0 && (
        <p className="muted small">Nothing matched here yet.</p>
      )}

      {row.matches.length > 0 && (
        <ul className="review-matches">
          {row.matches.map((match, index) => (
            <MatchRow
              key={match.id}
              match={match}
              ingredient={row.name}
              /* Alternates come after the chosen one within each store, so a
                 store label only needs repeating when the store changes. */
              newStore={index === 0 || row.matches[index - 1].store !== match.store}
            />
          ))}
        </ul>
      )}

      {!rematch.isPending && empty.length > 0 && (
        <p className="muted small">{empty.join(' · ')}</p>
      )}
      {rematch.isError && (
        <p className="muted small">
          Could not search the shops again: {(rematch.error as Error).message}
        </p>
      )}

      <IngredientEditor
        row={row}
        onSaved={() => rematch.mutate({ ingredient: row.id })}
      />
      <ProductFinder ingredientId={row.id} ingredientName={row.name} storeId={storeId} />
    </article>
  );
}

export default function MatchReview() {
  /* Arriving from a shopping-list line means arriving with a question about
     one ingredient at one shop, so the link carries both: ?search=panko lands
     on that row rather than on the top of the queue, and ?store= keeps the
     shop the list was built for — the default is whichever shop is first by
     priority, which is the wrong shelf to answer about. Scope follows too, or
     the row is filtered straight back out for having been answered already. */
  const [params] = useSearchParams();
  const asked = params.get('search') ?? '';
  const askedStore = Number(params.get('store')) || null;

  const [search, setSearch] = useState(asked);
  const [scope, setScope] = useState<'unconfirmed' | 'unmatched' | 'all'>(
    asked ? 'all' : 'unconfirmed',
  );
  const [page, setPage] = useState(1);
  const [storeId, setStoreId] = useState<number | null>(askedStore);
  /* A premade blend is answered by describing it rather than by finding a
     product for it, so the two halves of the queue can be worked separately. */
  const [described, setDescribed] = useState(true);
  const [undescribed, setUndescribed] = useState(true);
  const substitution = described === undescribed ? null : described ? 'yes' : 'no';
  const noneWanted = !described && !undescribed;

  const { data: stores } = useStores();
  /* Falls back to the first store, which is ordered by priority — 0 is the
     shop you actually shop at. */
  const activeStore = storeId ?? stores?.[0]?.id ?? null;

  const { data, isLoading } = useMatchQueue({
    search,
    page,
    store: activeStore,
    unconfirmed: scope === 'unconfirmed',
    unmatched: scope === 'unmatched',
    substitution,
  }, !noneWanted);

  const rows = data?.results ?? [];
  const total = data?.count ?? 0;

  function refilter(apply: () => void) {
    apply();
    setPage(1);
  }

  return (
    <section className="review">
      <h1>Is this the right product?</h1>
      <div className="review-controls">
        <label className="review-store-pick">
          <span>Shop</span>
          <select
            value={activeStore ?? ''}
            aria-label="Which shop to review matches for"
            onChange={(event) => refilter(() => setStoreId(Number(event.target.value)))}
          >
            {(stores ?? []).map((store) => (
              <option key={store.id} value={store.id}>
                {store.name}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          value={search}
          placeholder="Search by ingredient"
          aria-label="Search ingredients"
          onChange={(event) => refilter(() => setSearch(event.target.value))}
        />
        <label>
          <input
            type="radio"
            name="scope"
            checked={scope === 'unconfirmed'}
            onChange={() => refilter(() => setScope('unconfirmed'))}
          />
          Proposed here, not yet confirmed
        </label>
        <label>
          <input
            type="radio"
            name="scope"
            checked={scope === 'unmatched'}
            onChange={() => refilter(() => setScope('unmatched'))}
          />
          Nothing found at this shop
        </label>
        <label>
          <input
            type="radio"
            name="scope"
            checked={scope === 'all'}
            onChange={() => refilter(() => setScope('all'))}
          />
          Everything
        </label>
        <label>
          <input
            type="checkbox"
            checked={described}
            onChange={(event) => refilter(() => setDescribed(event.target.checked))}
          />
          Has a substitution recipe
        </label>
        <label>
          <input
            type="checkbox"
            checked={undescribed}
            onChange={(event) => refilter(() => setUndescribed(event.target.checked))}
          />
          No substitution recipe
        </label>
      </div>

      {isLoading && <p className="center-note">Loading…</p>}
      {!isLoading && rows.length === 0 && (
        <p className="muted">Nothing matches those filters.</p>
      )}

      {rows.map((row) => (
        <IngredientBlock key={row.id} row={row} storeId={activeStore} />
      ))}

      <Pager
        page={page}
        total={total}
        pageSize={PAGE_SIZE}
        hasNext={Boolean(data?.next)}
        onPage={setPage}
        noun="ingredients"
      />
    </section>
  );
}
